import React, { FC } from 'react';
import { Invoice, InvoiceStatus } from '../../types/schema';

import styled from 'styled-components';
import SVGIcon from '../SVGIcon/SVGIcon';
import OpenLink from './OpenLink.svg';
import { Viewport } from 'components/ViewportProvider';
import messages from './messages';
import translations from 'translations';

const Container = styled.div`
  padding: 12px 16px;
  background: #ffffff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const InvoiceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InvoiceInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0px;
  }
`;

const InvoiceId = styled.span`
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  color: #343538;
  @media (max-width: 767px) {
    font-size: 14px;
  }
`;

const InvoiceLabel = styled.span`
  color: #343538;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  @media (max-width: 767px) {
    font-size: 14px;
  }
`;

const InvoiceDate = styled.span`
  color: #808289;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  @media (max-width: 767px) {
    font-size: 12px;
  }
`;

const InvoiceTotal = styled.span`
  color: #808289;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  @media (max-width: 767px) {
    font-size: 12px;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const ViewButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: #fff;
  border: 1px solid #eaeaeb;
  border-radius: 6px;
  padding: 8px 16px;
  color: #343538;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: border 0.2s;
  &:hover {
    border: 1px solid #43a047;
    color: #43a047;
    svg {
      color: #43a047 !important;
    }
  }
  @media (max-width: 767px) {
    border: none;
    font-size: 12px;
    color: #388bff;
    padding: 0px;
    svg {
      width: 16px !important;
      color: #388bff !important;
    }
  }
`;

interface InvoiceInfoProps {
  invoice: Invoice;
}

const InvoiceInfo: FC<InvoiceInfoProps> = ({ invoice }) => {
  const handleViewInvoice = () => {
    if (invoice.uuid) {
      window.open(`${process.env.API_URL}/invoices/${invoice.uuid}/file/redirect`, '_blank');
    }
  };
  const ActionComponent = () => (
    <>
      {invoice.uuid && (
        <Actions>
          <ViewButton onClick={handleViewInvoice}>
            <SVGIcon src={OpenLink} renderAsImg={false} />
            {translations(messages.viewInvoice)}
          </ViewButton>
        </Actions>
      )}
    </>
  );
  if (invoice?.invoiceStatus !== InvoiceStatus.Issued) {
    return null;
  }
  return (
    <Viewport.Consumer>
      {({ width }) => {
        const isMobileSize = width < 768;
        return (
          <Container>
            <Info>
              <HeaderWrapper>
                <InvoiceRow>
                  <InvoiceLabel>{translations(messages.invoice)}:</InvoiceLabel>
                  <InvoiceId>{invoice?.fastInvoiceNo}</InvoiceId>
                </InvoiceRow>
                {isMobileSize && <ActionComponent />}
              </HeaderWrapper>
              <InvoiceInfoRow>
                <InvoiceDate>
                  {translations(messages.issuedDate)}: {formatDate(invoice.invoiceDate)}
                </InvoiceDate>
                {!isMobileSize && <span style={{ color: '#D9D9D9' }}>•</span>}
                <InvoiceTotal>
                  {translations(messages.total)}: {formatMoney(invoice.total)}đ
                </InvoiceTotal>
              </InvoiceInfoRow>
            </Info>
            {!isMobileSize && <ActionComponent />}
          </Container>
        );
      }}
    </Viewport.Consumer>
  );
};

function formatDate(date: string) {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('vi-VN');
}

function formatMoney(amount: number) {
  return amount?.toLocaleString('vi-VN');
}

export default InvoiceInfo;
